import {
  BrowserRouter as Router,
  Route,
  Routes,
  Navigate,
} from "react-router-dom";
import { useEffect } from "react";
import { MantineProvider, createTheme } from "@mantine/core";
import AdminLayout from "./components/Layout/AdminLayout";
import UnauthenticatedLayout from "./components/Layout/UnauthenticatedLayout";
import Login from "./pages/Login/Login";
import Registration from "./pages/Registration/Registration";
import Analytics from "./pages/Admin/Analytics/Analytics";
import Magazines from "./pages/Admin/Magazines/Magazines";
import Publications from "./pages/Admin/Publications/Publications";
import Catalogue from "./pages/User/Catalogue/Catalogue";
import UserDetails from "./pages/Admin/UserDetails/UserDetails";
import Users from "./pages/Admin/Users/Users";
import "@mantine/core/styles.css";
import "@mantine/charts/styles.css";
import "@mantine/dropzone/styles.css";
import "@mantine/dates/styles.css";
import "@mantine/carousel/styles.css";
import "@mantine/notifications/styles.css";
import UserLayout from "./components/Layout/UserLayout";
import Cart from "./pages/User/Cart/Cart";
import Profile from "./pages/User/Profile/Profile";
import { CartProvider } from "./context/CartContext";
import { useMe } from "./context/MeContext";
import { Notifications } from "@mantine/notifications";
import Loading from "./components/Loading/Loading";
import ForgottenPassword from "./pages/PasswordRecovery/ForgottenPassword";
import ResetPassword from "./pages/PasswordRecovery/ResetPassword";
import PasswordResetSuccess from "./pages/PasswordRecovery/PasswordResetSuccess";
import UserPublications from "./pages/User/Publications/Publications";
import PaymentSuccess from "./pages/User/Payment/PaymentSuccess";
import PaymentError from "./pages/User/Payment/PaymentError";
import PageNotFound from "./pages/PageNotFound/PageNotFound";
import Terms from "./pages/User/TermsAndPrivacy/Terms";
import PrivacyPolicy from "./pages/User/TermsAndPrivacy/PrivacyPolicy";
import AboutUs from "./pages/User/AboutUs/AboutUs";
import MobileLogin from "./pages/MobileLogin/MobileLogin";

const theme = createTheme({
  fontFamily: "Montserrat, sans-serif",
});

function App() {
  useEffect(() => {
    const setVH = () => {
      const vh = window.innerHeight * 0.01;
      document.documentElement.style.setProperty("--vh", `${vh}px`);
    };
    setVH();
    window.addEventListener("resize", setVH);
    return () => {
      window.removeEventListener("resize", setVH);
    };
  }, []);

  const { me, loading } = useMe();

  if (loading)
    return (
      <MantineProvider theme={theme}>
        <Loading />
      </MantineProvider>
    );

  const isAuthenticated = !!me;
  const isAdmin = me?.type === "ADMIN";
  return (
    <MantineProvider theme={theme}>
      <Notifications />
      <Router>
        <Routes>
          <Route
            path="/"
            element={
              isAuthenticated ? (
                <Navigate
                  to={isAdmin ? "/admin/analytics" : "/catalogue"}
                  replace
                />
              ) : (
                <Navigate to="/login" replace />
              )
            }
          />
          <Route
            path="/login"
            element={
              <UnauthenticatedLayout>
                <Login />
              </UnauthenticatedLayout>
            }
          />
          <Route
            path="/registration"
            element={
              <UnauthenticatedLayout>
                <Registration />
              </UnauthenticatedLayout>
            }
          />
          <Route
            path="/forgotten-password"
            element={
              <UnauthenticatedLayout>
                <ForgottenPassword />
              </UnauthenticatedLayout>
            }
          />
          <Route
            path="/reset-password"
            element={
              <UnauthenticatedLayout>
                <ResetPassword />
              </UnauthenticatedLayout>
            }
          />
          <Route
            path="/password-reset-success"
            element={
              <UnauthenticatedLayout>
                <PasswordResetSuccess />
              </UnauthenticatedLayout>
            }
          />
          <Route
            path="/mobile-login"
            element={
              <UnauthenticatedLayout>
                <MobileLogin />
              </UnauthenticatedLayout>
            }
          />
          <Route
            path="/admin/analytics"
            element={
              <AdminLayout>
                <Analytics />
              </AdminLayout>
            }
          />
          <Route
            path="/admin/magazines"
            element={
              <AdminLayout>
                <Magazines />
              </AdminLayout>
            }
          />
          <Route
            path="/admin/publications"
            element={
              <AdminLayout>
                <Publications />
              </AdminLayout>
            }
          />
          <Route
            path="/admin/users"
            element={
              <AdminLayout>
                <Users />
              </AdminLayout>
            }
          />
          <Route
            path="/admin/users/:id"
            element={
              <AdminLayout>
                <UserDetails />
              </AdminLayout>
            }
          />

          <Route
            path="/catalogue"
            element={
              <CartProvider>
                <UserLayout>
                  <Catalogue />
                </UserLayout>
              </CartProvider>
            }
          />
          <Route
            path="/publications"
            element={
              <CartProvider>
                <UserLayout>
                  <UserPublications />
                </UserLayout>
              </CartProvider>
            }
          />
          <Route
            path="/cart"
            element={
              <CartProvider>
                <UserLayout>
                  <Cart />
                </UserLayout>
              </CartProvider>
            }
          />

          <Route
            path="/profile"
            element={
              <CartProvider>
                <UserLayout>
                  <Profile />
                </UserLayout>
              </CartProvider>
            }
          />
          <Route
            path="/terms"
            element={
              <CartProvider>
                <UserLayout>
                  <Terms />
                </UserLayout>
              </CartProvider>
            }
          />
          <Route
            path="/privacy"
            element={
              <CartProvider>
                <UserLayout>
                  <PrivacyPolicy />
                </UserLayout>
              </CartProvider>
            }
          />
          <Route
            path="/about-us"
            element={
              <CartProvider>
                <UserLayout>
                  <AboutUs />
                </UserLayout>
              </CartProvider>
            }
          />

          <Route
            path="/payment/success"
            element={
              <CartProvider>
                <PaymentSuccess />
              </CartProvider>
            }
          />
          <Route
            path="/payment/error"
            element={
              <CartProvider>
                <PaymentError />
              </CartProvider>
            }
          />
          <Route path="*" element={<PageNotFound />} />
        </Routes>
      </Router>
    </MantineProvider>
  );
}

export default App;
